import React, { useState, useEffect } from 'react';
import { DeskLayout } from '../../components/DeskLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { getGuests, getProperties, createGuest, updateGuest, getConversations } from '../../lib/api';
import { Search, Plus, User, Mail, Phone, Tag, MessageSquare } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export const Guests = () => {
  const [loading, setLoading] = useState(true);
  const [guests, setGuests] = useState([]);
  const [properties, setProperties] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProperty, setFilterProperty] = useState('all');
  const [showNewGuest, setShowNewGuest] = useState(false);
  const [newGuestData, setNewGuestData] = useState({
    fullName: '',
    email: '',
    phone: '',
    propertyId: '',
    tags: [],
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [guestsRes, propsRes, convsRes] = await Promise.all([
        getGuests(),
        getProperties(),
        getConversations()
      ]);
      setGuests(guestsRes.data);
      setProperties(propsRes.data);
      setConversations(convsRes.data);
    } catch (error) {
      console.error('Failed to load guests:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPropertyName = (propertyId) => properties.find(p => p.id === propertyId)?.name || 'Unknown';
  
  const getGuestConversations = (guestId) => conversations.filter(c => c.guestId === guestId);

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'G';

  const handleCreateGuest = async () => {
    if (!newGuestData.fullName || !newGuestData.propertyId) {
      toast.error('Please fill required fields');
      return;
    }
    try {
      await createGuest(newGuestData);
      setShowNewGuest(false);
      setNewGuestData({ fullName: '', email: '', phone: '', propertyId: '', tags: [], notes: '' });
      await loadData();
      toast.success('Guest added');
    } catch (error) {
      toast.error('Failed to add guest');
    }
  };

  const handleTagToggle = (tag) => {
    const tags = newGuestData.tags.includes(tag)
      ? newGuestData.tags.filter(t => t !== tag)
      : [...newGuestData.tags, tag];
    setNewGuestData({ ...newGuestData, tags });
  };

  const filteredGuests = guests.filter(g => {
    const matchesSearch = !searchTerm || 
      g.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      g.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProperty = filterProperty === 'all' || g.propertyId === filterProperty;
    return matchesSearch && matchesProperty;
  });

  if (loading) {
    return (
      <DeskLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Skeleton className="lg:col-span-2 h-96" />
            <Skeleton className="h-96" />
          </div>
        </div>
      </DeskLayout>
    );
  }

  return (
    <DeskLayout>
      <div className="space-y-6 animate-fade-in" data-testid="guests-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-medium text-stone-900">Guests</h1>
            <p className="text-stone-500 mt-1">Manage your guest directory</p>
          </div>
          <Dialog open={showNewGuest} onOpenChange={setShowNewGuest}>
            <DialogTrigger asChild>
              <Button className="bg-[#3F617E] hover:bg-[#345470] rounded-full" data-testid="add-guest-button">
                <Plus className="h-4 w-4 mr-1" /> Add Guest
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">Add New Guest</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input 
                    value={newGuestData.fullName} 
                    onChange={(e) => setNewGuestData({...newGuestData, fullName: e.target.value})}
                    placeholder="Guest name"
                    data-testid="new-guest-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Property *</Label>
                  <Select value={newGuestData.propertyId} onValueChange={(v) => setNewGuestData({...newGuestData, propertyId: v})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select property" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input 
                      type="email"
                      value={newGuestData.email} 
                      onChange={(e) => setNewGuestData({...newGuestData, email: e.target.value})}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input 
                      value={newGuestData.phone} 
                      onChange={(e) => setNewGuestData({...newGuestData, phone: e.target.value})}
                      placeholder="+1 234 567 8900"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Tags</Label>
                  <div className="flex flex-wrap gap-2">
                    {['VIP', 'Repeat', 'Sensitive'].map(tag => (
                      <Badge 
                        key={tag}
                        variant={newGuestData.tags.includes(tag) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => handleTagToggle(tag)}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea 
                    value={newGuestData.notes} 
                    onChange={(e) => setNewGuestData({...newGuestData, notes: e.target.value})}
                    placeholder="Any special notes about this guest..."
                    rows={3}
                  />
                </div>
                <Button onClick={handleCreateGuest} className="w-full bg-[#3F617E] hover:bg-[#345470] rounded-full">
                  Add Guest
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input 
              placeholder="Search guests..." 
              className="pl-9"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="guest-search"
            />
          </div>
          <Select value={filterProperty} onValueChange={setFilterProperty}>
            <SelectTrigger className="w-48" data-testid="filter-property-select">
              <SelectValue placeholder="Filter by property" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Properties</SelectItem>
              {properties.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Guest List */}
          <Card className="lg:col-span-2 border-stone-200">
            <CardContent className="p-0">
              <div className="divide-y divide-stone-100">
                {filteredGuests.map((guest) => (
                  <button
                    key={guest.id}
                    onClick={() => setSelectedGuest(guest)}
                    className={`w-full text-left p-4 hover:bg-stone-50 transition-colors ${
                      selectedGuest?.id === guest.id ? 'bg-stone-50' : ''
                    }`}
                    data-testid={`guest-item-${guest.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12 bg-[#3F617E]">
                        <AvatarFallback className="bg-[#3F617E] text-white">
                          {getInitials(guest.fullName)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-stone-900">{guest.fullName}</p>
                          {guest.tags?.map(tag => (
                            <Badge key={tag} variant="secondary" className="text-xs">{tag}</Badge>
                          ))}
                        </div>
                        <p className="text-sm text-stone-500">{getPropertyName(guest.propertyId)}</p>
                      </div>
                      <div className="text-right text-sm text-stone-400">
                        {format(new Date(guest.createdAt), 'MMM d, yyyy')}
                      </div>
                    </div>
                  </button>
                ))}
                {filteredGuests.length === 0 && (
                  <div className="p-12 text-center">
                    <User className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                    <p className="text-stone-500">No guests found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Guest Detail */}
          <Card className="border-stone-200">
            <CardHeader className="pb-4">
              <CardTitle className="font-heading text-lg">Guest Details</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedGuest ? (
                <div className="space-y-6">
                  <div className="text-center">
                    <Avatar className="h-20 w-20 mx-auto bg-[#3F617E]">
                      <AvatarFallback className="bg-[#3F617E] text-white text-2xl">
                        {getInitials(selectedGuest.fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <h3 className="font-heading text-xl mt-4">{selectedGuest.fullName}</h3>
                    <p className="text-sm text-stone-500">{getPropertyName(selectedGuest.propertyId)}</p>
                    <div className="flex justify-center gap-2 mt-2">
                      {selectedGuest.tags?.map(tag => (
                        <Badge key={tag} variant="secondary">{tag}</Badge>
                      ))}
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {selectedGuest.email && (
                      <div className="flex items-center gap-3 text-sm">
                        <Mail className="h-4 w-4 text-stone-400" />
                        <span>{selectedGuest.email}</span>
                      </div>
                    )}
                    {selectedGuest.phone && (
                      <div className="flex items-center gap-3 text-sm">
                        <Phone className="h-4 w-4 text-stone-400" />
                        <span>{selectedGuest.phone}</span>
                      </div>
                    )}
                  </div>
                  
                  {selectedGuest.notes && (
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-sm text-stone-600">{selectedGuest.notes}</p>
                    </div>
                  )}
                  
                  <div>
                    <h4 className="text-sm font-medium text-stone-700 mb-2">Conversations</h4>
                    <div className="space-y-2">
                      {getGuestConversations(selectedGuest.id).slice(0, 3).map(conv => (
                        <div key={conv.id} className="flex items-center gap-2 text-sm text-stone-600">
                          <MessageSquare className="h-4 w-4 text-stone-400" />
                          <span className="truncate">{conv.subject}</span>
                        </div>
                      ))}
                      {getGuestConversations(selectedGuest.id).length === 0 && (
                        <p className="text-sm text-stone-400">No conversations yet</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <User className="h-12 w-12 text-stone-300 mx-auto mb-3" />
                  <p className="text-stone-500">Select a guest to view details</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </DeskLayout>
  );
};

export default Guests;
